/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { forwardRef, Ref, RefAttributes, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import Grid from "@mui/material/Grid";
import { Ratio } from "@simplyk/common";
import dynamic from "next/dynamic";
import { FieldValues } from "react-hook-form";

import { CropDialog } from "../Crop/CropDialog";
import { useCustomAssetLibraryImages } from "../SelectImageDialog/hooks/useCustomAssetLibraryImages";
import { ImageSelectionResult } from "../SelectImageDialog/types";

import { resolveEditorConfig } from "./helpers/resolveEditorConfig";
import { useImageEditor } from "./helpers/useImageEditor";
import { useImageMetadata } from "./helpers/useImageMetadata";
import { useImageSourceSelection } from "./helpers/useImageSourceSelection";
import { useImageUpload } from "./helpers/useImageUpload";
import { useImageUploaderField } from "./helpers/useImageUploaderField";
import { useImageUploaderSizing } from "./helpers/useImageUploaderSizing";
import { EmptyState } from "./screens/EmptyState";
import { FilledActions } from "./screens/FilledActions";
import { LoadingOverlay } from "./screens/LoadingOverlay";
import { SquareEditorOverlay } from "./screens/SquareEditorOverlay";
import {
  ImageUploaderProps,
  ImageUploaderHandle,
  ImageUploaderEmptyState,
  ImageUploaderEmptyStateAction,
} from "./types";

// Dynamically import SelectImageDialog to prevent SSR issues with Unsplash tRPC calls
const SelectImageDialog = dynamic(
  () => import("../SelectImageDialog/SelectImageDialog").then((mod) => mod.SelectImageDialog),
  {
    ssr: false,
  }
);

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardCard } from "@/dashboard-design-system/Card/DashboardCard";
import { zeffyCardClasses } from "@/dashboard-design-system/Card/types";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

export const ImageUploader = forwardRef(ImageUploaderInner) as unknown as <T extends FieldValues>(
  props: ImageUploaderProps<T> & RefAttributes<ImageUploaderHandle>
) => React.ReactElement<ImageUploaderProps<T>>;

export function ImageUploaderInner<T extends FieldValues>(
  {
    "data-test": dataTest,
    disabled = false,
    library,
    showDeleteButton = true,
    className,
    emptyState,
    onBeforeReplace,
    onBeforeDelete,
    onImageSourceChange,
    onImageAltTextMetadataChange,
    field,
    editor: editorConfig,
    layout,
    upload,
    labels,
  }: ImageUploaderProps<T>,
  ref: Ref<ImageUploaderHandle>
): React.ReactElement<ImageUploaderProps<T>> {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const { logAmplitudeEvent } = useAmplitude();

  const { form, name, isUploadingName, required } = field;
  const { ratio, maxHeight, displayAspectRatio } = layout;
  const {
    context: uploadContext,
    preset: uploadPreset = "default",
    amplitudePayload,
    fundraiserContext,
    persistToAssetLibrary = true,
  } = upload;
  const importLabel = labels.import;
  const selectImageTitle = labels.selectImageTitle;

  const showImageLibrary = library !== undefined;
  const allowZeffyImages = library?.allowZeffyImages ?? false;
  const allowUnsplash = library?.allowUnsplash ?? false;
  const defaultImageLibraryTab = library?.defaultTab;
  const excludedZeffyImageTags = library?.excludedZeffyImageTags;
  const libraryPresentation = library?.presentation ?? "dialog";

  // Merge upload context with additional amplitude payload
  const fullAmplitudePayload = useMemo(
    () => ({
      upload_context: uploadContext,
      ...amplitudePayload,
    }),
    [uploadContext, amplitudePayload]
  );
  const { errors } = form.formState;

  const { organization } = useCurrentUserContext();
  const isCustomAssetLibraryActive = Boolean(organization);

  const { images: customAssetLibraryImages, isLoading: isCustomAssetLibraryLoading } = useCustomAssetLibraryImages({
    enabled: showImageLibrary && isCustomAssetLibraryActive,
  });
  const hasMyImages = !isCustomAssetLibraryLoading && customAssetLibraryImages.length > 0;
  const skipToFilePicker = !allowZeffyImages && !isCustomAssetLibraryLoading && !hasMyImages;

  const {
    useSquareEditor,
    targetSize: squareTargetSize,
    centerFit: squareCenterFit,
    whiteBackground: squareWhiteBackground,
    previewSize,
    showRatioPresets,
    maxDimensions,
    preserveTransparency,
    disableCrop,
    keepGifAnimation,
  } = resolveEditorConfig(editorConfig, isSmallScreen);

  const { fileUrl, setFileUrl, value, isUploading } = useImageUploaderField({ form, name, isUploadingName, required });

  const [imageSourceMetadata, setImageSourceMetadata] = useState<ImageSelectionResult | null>(null);
  const [selectImageModalOpen, setSelectImageModalOpen] = useState(false);

  const { actualImageMetaData, setActualImageMetaData, fillImageMetadata, displayRatioValue } = useImageMetadata({
    fileUrl,
    ratio,
    displayAspectRatio,
  });

  const { updateFileUrl } = useImageUpload({
    form,
    name,
    isUploadingName,
    uploadPreset,
    uploadContext,
    fundraiserContext,
    fullAmplitudePayload,
    isCustomAssetLibraryActive,
    persistToAssetLibrary,
    imageSourceMetadata,
    setImageSourceMetadata,
    setFileUrl,
    fillImageMetadata,
    onImageSourceChange,
    onImageAltTextMetadataChange,
  });

  const editor = useImageEditor({
    form,
    name,
    isUploadingName,
    setFileUrl,
    updateFileUrl,
    fillImageMetadata,
    onImageSourceChange,
    setSelectImageModalOpen,
  });

  const selection = useImageSourceSelection({
    form,
    name,
    fileUrl,
    setFileUrl,
    autoFitToSquare: useSquareEditor,
    disableCrop,
    keepGifAnimation,
    updateFileUrl,
    openCropEditor: editor.openCropEditor,
    openSquareEditor: editor.openSquareEditor,
    setImageSourceMetadata,
    setSelectImageModalOpen,
    onBeforeReplace,
    onBeforeDelete,
    onImageSourceChange,
    actualImageMetaData,
    fullAmplitudePayload,
    showImageLibrary,
    allowZeffyImages,
    isCustomAssetLibraryLoading,
    hasMyImages,
  });

  // Resets fileUrl only when imageUrl is null, handling banner type switches in Live Form Editor without affecting uploads.
  useEffect(() => {
    if (value === null) {
      setFileUrl(undefined);
      setActualImageMetaData(undefined);
    }
  }, [value, setActualImageMetaData, setFileUrl]);

  const onFileChangeRef = useRef(selection.onFileChange);
  onFileChangeRef.current = selection.onFileChange;
  const openSelectImageModalRef = useRef(selection.openSelectImageModal);
  openSelectImageModalRef.current = selection.openSelectImageModal;

  useImperativeHandle(
    ref,
    () => ({
      setImage: (url: string | null) => {
        setFileUrl(url || undefined);
        if (!url) {
          editor.resetEditor();
        }
      },
      finalizeSquareCrop: () => editor.finalize(),
      openSelectImageModal: () => {
        void openSelectImageModalRef.current();
      },
      uploadFile: (file: File) => {
        onFileChangeRef.current(file);
      },
    }),
    [editor, setFileUrl]
  );

  const { imageUploaderHeight, handleSetImageUploaderRef } = useImageUploaderSizing({
    ratio,
    displayRatioValue,
    maxHeight,
  });

  const defaultEmptyStateActions: ImageUploaderEmptyStateAction[] = [{ type: "upload" }];
  if (showImageLibrary && !skipToFilePicker) {
    defaultEmptyStateActions.push({ type: "selectFromLibrary" });
  }
  const resolvedEmptyState: ImageUploaderEmptyState = emptyState ?? { actions: defaultEmptyStateActions };
  const emptyStateActions = resolvedEmptyState.hidden ? [] : resolvedEmptyState.actions;

  const getEmptyStateActionConfig = (action: ImageUploaderEmptyStateAction): { label: string; onClick: () => void } => {
    switch (action.type) {
      case "upload":
        return { label: action.label ?? importLabel, onClick: selection.handlePickImage };
      case "selectFromLibrary":
        return { label: action.label ?? t("common", "selectImage"), onClick: selection.openSelectImageModal };
      case "fillWithPlaceholder":
        return { label: action.label ?? t("dashboard", "fillWithPlaceholderBanner"), onClick: action.onSelect };
    }
  };

  return (
    <>
      {editor.isCropOpen && (
        <CropDialog
          isOpen={editor.isCropOpen}
          onCrop={editor.confirmCrop}
          onClose={editor.closeCrop}
          croppedFile={editor.croppedFile}
          ratio={displayRatioValue}
          maxDimensions={maxDimensions}
          actualImageMetaData={actualImageMetaData}
          fillImageMetadata={fillImageMetadata}
          handlePickImage={selection.handlePickImage}
          amplitudePayload={fullAmplitudePayload}
          selectImageModalOpen={selectImageModalOpen}
          showRatioPresets={showRatioPresets}
        />
      )}
      {selectImageModalOpen && (
        <SelectImageDialog
          isOpen={selectImageModalOpen}
          onClose={selection.closeSelectImageModal}
          onSelectImage={selection.handleImageSelection}
          onNewImageUpload={selection.handleNewImageUpload}
          amplitudePayload={fullAmplitudePayload}
          isSquareRatio={useSquareEditor}
          title={selectImageTitle}
          allowZeffyImages={allowZeffyImages}
          allowUnsplash={allowUnsplash}
          excludedZeffyImageTags={excludedZeffyImageTags}
          defaultTab={isCustomAssetLibraryActive ? defaultImageLibraryTab : "zeffyImages"}
          customAssetLibraryImages={customAssetLibraryImages}
          isCustomAssetLibraryLoading={isCustomAssetLibraryLoading}
          presentation={libraryPresentation}
        />
      )}
      <DashboardCard
        variant="outlined"
        fullWidth
        noHover
        className={className}
        data-test={dataTest}
        ref={handleSetImageUploaderRef}
        style={{ height: ratio === Ratio.Free ? (maxHeight ?? undefined) : imageUploaderHeight }}
        sx={(theme) => ({
          [`&.${zeffyCardClasses.root}`]: {
            position: "relative",
            height: ratio === Ratio.Free ? (maxHeight ? "100%" : undefined) : "100%",
            backgroundColor: theme.palette.surface.neutral.supershy,
            borderColor: theme.palette.border.neutral.moderate,
            borderStyle: "dashed",
            overflow: "hidden",
            padding: 0,
            "&:hover": {
              backgroundColor: theme.palette.surface.neutral.supershy,
            },
          },
        })}
      >
        <input
          name={name}
          accept="image/*"
          style={{ display: "none" }}
          id={name}
          type="file"
          onChange={selection.onInputFileChange}
          ref={selection.inputRef}
          disabled={disabled}
        />
        <Grid
          container
          sx={{
            cursor: disabled ? "disabled" : "pointer",
            backgroundImage: `url(${fileUrl})`,
            backgroundSize: ratio === Ratio.Auto || ratio === Ratio.Free ? "contain" : "cover",
            backgroundPosition: "center",
            backgroundRepeat: ratio === Ratio.Auto || ratio === Ratio.Free ? "no-repeat" : undefined,
            height: "100%",
            minHeight: ratio === Ratio.Free ? undefined : imageUploaderHeight,
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: 0,
            width: "100%",
          }}
          justifyContent="center"
          alignItems="center"
        >
          {editor.isSquareEditorOpen && editor.squareEditorImageUrl && (
            <SquareEditorOverlay
              editorRef={editor.squareImageEditorRef}
              imageDataUrl={editor.squareEditorImageUrl}
              targetSize={squareTargetSize}
              previewSize={previewSize}
              onConfirm={editor.confirmSquareEditor}
              onPreviewUpdate={editor.updateSquareEditorPreview}
              onReplace={editor.replaceSquareEditor}
              onDelete={editor.deleteSquareEditor}
              preserveTransparency={preserveTransparency}
              showCenterFitButton={squareCenterFit}
              useWhiteBackground={squareWhiteBackground}
              onFitToggle={(newFitMode) => {
                logAmplitudeEvent(AmplitudeEvents.ImageUploaderFitToggled, {
                  is_fit_mode: newFitMode,
                  ...fullAmplitudePayload,
                });
              }}
            />
          )}
          {isUploading && fileUrl && <LoadingOverlay />}
          {!fileUrl && emptyStateActions.length > 0 && (
            <EmptyState actions={emptyStateActions} getActionConfig={getEmptyStateActionConfig} dataTest={dataTest} />
          )}
          {fileUrl && (
            <FilledActions
              onReplace={showImageLibrary ? selection.openSelectImageModal : selection.handlePickImage}
              onDelete={selection.onDelete}
              showDeleteButton={showDeleteButton}
              dataTest={dataTest}
            />
          )}
        </Grid>
      </DashboardCard>

      {errors?.[name] && (
        <DashboardTypography variant="body2" sx={{ color: "error.main" }}>
          {String(errors[name]?.message || "")}
        </DashboardTypography>
      )}

      {/* If the user try to upload an image but the image is still uploading, we show an error */}
      {errors?.[isUploadingName] && (
        <DashboardTypography variant="body2" sx={{ color: "error.main" }}>
          {String(errors[isUploadingName]?.message || "")}
        </DashboardTypography>
      )}
    </>
  );
}
